"use client";
import { InfoIcon } from "lucide-react";
import {
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Sheet,
  SheetDescription,
  SheetFooter,
} from "@workspace/ui/components/sheet";

import iphoneCategories from "@/lib/data/iphone-categories.json";
import iphoneModels from "@/lib/data/iphone-models.json";
import React, { useEffect } from "react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@workspace/ui/components/tooltip";

import {
  RadioGroup,
  RadioGroupItem,
} from "@workspace/ui/components/radio-group";
import { useRouter } from "next/navigation";
import {
  sendEvent,
  EventNames,
  SectionTypes,
  withSectionAndFlow,
} from "@/lib/utils/analytics";
import { FALLBACK_DISCOUNT_CODES, OS } from "@/lib/utils/common/constants";
import { buildCheckoutUrl } from "@/lib/utils/common/checkout-url";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import ChevronRight from "@workspace/ui/icons/chevron-right";
import { useModal } from "../providers/modal-provider";
import { modalIds } from "./constants";
import { useApp } from "../providers/app-provider";

export const OsSelectionSheet = () => {
  const [selectedOs, setSelectedOs] = React.useState<string | null>("android");
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const { modalId, handleCloseModal, handleOpenModal, modalContent } =
    useModal();

  const isOpen = modalId === modalIds.osSelection;

  const router = useRouter();

  useEffect(() => {
    if (!isOpen) return;
    sendEvent(EventNames.NG_SIDE_BANNER_VIEWED);
    sendEvent(EventNames.BUY_NOW_CLICKED, { flow_type: "NG" });
  }, [isOpen]);

  const handleOsSelect = (os: string) => {
    setSelectedOs(os);
    sendEvent(EventNames.PRODUCT_SELECTION_CLICKED, {
      flow_type: os === "android" ? "NG" : "OG",
    });
  };

  const handleOrderNowClick = () => {
    const params = new URLSearchParams(window.location.search);
    const sectionType =
      modalContent?.sectionType ?? SectionTypes.DRAWER;
    if (selectedOs === OS.ANDROID) {
      const checkoutPayload = withSectionAndFlow(sectionType, "ng", {
        flow: "Android",
      });
      sendEvent(EventNames.CHECKOUT_INTERMEDIATE_CLICKED, checkoutPayload);
      sendEvent(EventNames.CHECKOUT_NOW_CLICKED, checkoutPayload);
      sendEvent(EventNames.FLOW_NEXT_CLICKED, { flow: "Android" });
      setIsRedirecting(true);
      window.location.href = buildCheckoutUrl(
        "inito-fertility-monitor-ng",
        `disc_code=${modalContent?.couponCode || FALLBACK_DISCOUNT_CODES["ng"]}&${params.toString()}`,
      );
      return;
    }
    const iosPayload = withSectionAndFlow(sectionType, "og", { flow: "IOS" });
    sendEvent(EventNames.CHECKOUT_INTERMEDIATE_CLICKED, iosPayload);
    sendEvent(EventNames.FLOW_NEXT_CLICKED, { flow: "IOS" });
    setSelectedOs(null);
    handleOpenModal(modalIds.phoneCategory, modalContent);
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleCloseModal}>
      <SheetContent
        className="&>button:first-of-type]:hidden flex h-full flex-col gap-6 px-0 py-0"
        sheetCloseTestId="modal-close-0"
      >
        <SheetHeader className="flex flex-col gap-3 px-5 pt-10 text-left md:px-6">
          <SheetTitle
            className="!text-[32px] !font-medium !leading-8 !text-primary"
            data-testid="os-selection-title"
            id="os-selection-title"
          >
            Please select your phone OS
          </SheetTitle>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-4 px-5 md:px-6">
          <RadioGroup
            value={selectedOs}
            onValueChange={handleOsSelect}
            name={`android`}
            defaultValue={"android"}
          >
            <label
              key={"android"}
              className="flex cursor-pointer items-center justify-between border-b border-b-[rgba(0,0,0,0.08)] px-1 py-4"
            >
              <span
                className="text-[17px] font-medium leading-[26px] tracking-tight text-primary"
                data-testid="android-radio-button"
                id="android-radio-button"
              >
                Android
              </span>
              <RadioGroupItem value={"android"} id={"android"} />
            </label>
            <label
              key={"ios"}
              className="flex cursor-pointer items-center justify-between border-b border-b-[rgba(0,0,0,0.08)] px-1 py-4 hover:cursor-pointer"
            >
              <span
                className="text-[17px] font-medium leading-[26px] tracking-tight text-primary"
                data-testid="ios-radio-button"
                id="ios-radio-button"
              >
                iOS
              </span>
              <RadioGroupItem value={"ios"} id={"ios"} />
            </label>
          </RadioGroup>
        </div>
        <SheetFooter className="w-full pb-4 md:pb-6">
          <Button
            disabled={!selectedOs || isRedirecting}
            className={cn(
              !selectedOs && "text-[rgba(192, 191, 189, 1)] bg-[#D4D1CE]",
              "h-full w-full py-4 rounded-[12px] text-base font-medium tracking-[-0.4px]"
            )}
            onClick={handleOrderNowClick}
            data-testid="os-selection-next-button"
            id="os-selection-next-button"
          >
            {isRedirecting ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Preparing your cart...
              </span>
            ) : selectedOs === OS.ANDROID ? (
              "Checkout now"
            ) : (
              "Next"
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export function OrderNowSheet() {
  const { modalId, handleCloseModal } = useModal();
  const isOpen = modalId === modalIds.phoneCategory;

  return (
    <Sheet open={isOpen} onOpenChange={handleCloseModal}>
      <SheetContent
        className="flex h-full flex-col gap-4"
        sheetCloseTestId="modal-close-1"
      >
        <SheetHeader className="flex flex-col gap-2 py-4 text-left pb-0 pt-10">
          <SheetTitle
            className="!text-[32px] !font-medium !leading-8 !text-primary"
            data-testid="choose-iphone-category-text"
            id="choose-iphone-category-text"
          >
            Select your iPhone category
          </SheetTitle>
          <SheetDescription asChild>
            <p className="!body-14 !text-secondary">
              Inito attaches to your smartphone using an attachment clip
              customized to your iPhone model.
              <Tooltip useTouch>
                <TooltipTrigger asChild>
                  <InfoIcon className="ml-1 inline h-[14px] w-[14px] cursor-pointer align-middle text-primary" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-primary text-xs font-normal leading-5 text-primary-foreground">
                  To find out which iPhone model you have, simply go to Settings
                  &gt; General &gt; About. You’ll see the model right next to
                  the Model Name.
                </TooltipContent>
              </Tooltip>
            </p>
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-4 overflow-auto px-5">
          {iphoneCategories.categories.map((category) => (
            <SelectCategory
              key={category.id}
              category={category}
              testId={`${category.id}-category`}
              closeParentSheet={handleCloseModal} // <-- pass down
            >
              <div className="flex justify-between border-b border-b-[rgba(0,0,0,0.08)] py-4 hover:cursor-pointer">
                <p className="text-[17px] font-medium leading-[26px] tracking-tight text-primary">
                  {category.name}
                </p>
                <ChevronRight className="h-6 w-6 text-primary" />
              </div>
            </SelectCategory>
          ))}
        </div>
        <SheetFooter>
          <p className="self-end text-[12px] font-medium leading-[18px] tracking-[-0.2px] text-grey">
            The First Generation Reader currently supports iPhone 7 and up.
            Android phones are not supported at the moment.
          </p>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

const SelectCategory = ({
  category,
  children,
  testId,
  closeParentSheet,
}: {
  category: any;
  children: React.ReactNode;
  testId: string;
  closeParentSheet: () => void;
}) => {
  const [selectedModel, setSelectedModel] = React.useState<any | null>(null);
  const [open, setOpen] = React.useState(false);
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const router = useRouter();
  const { modalContent } = useModal();
  const { workflow } = useApp();

  const handleCategorySelect = () => {
    setOpen(true);
    sendEvent(EventNames.PHONE_CATEGORY_SELECTED, {
      phone_category: category.name,
    });
  };

  const handleModelSelect = (selectedModel: any) => {
    setSelectedModel(selectedModel);
    sendEvent(EventNames.PHONE_MODEL_SELECTED, {
      phone_model: selectedModel.name,
    });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setOpen(false);
      closeParentSheet(); // close parent as well
      sendEvent(EventNames.CLOSE_BUTTON_ORDER_POPUP_CLICKED);
    } else {
      setOpen(true);
    }
  };

  const handleCheckout = () => {
    const params = new URLSearchParams(window.location.search);
    const sectionType =
      modalContent?.sectionType ?? SectionTypes.DRAWER;
    sendEvent(
      EventNames.CHECKOUT_NOW_CLICKED,
      withSectionAndFlow(sectionType, "og", {
        phone_model: selectedModel.name,
      }),
    );
    setIsRedirecting(true);

    if (workflow === "ng") {
      params.set("disc_code", FALLBACK_DISCOUNT_CODES["og"]);
      // OG / iPhone-model checkout stays on Rails — the Node checkout only
      // maintains the NG product, so OG slugs would 400.
      window.location.href = `${process.env.NEXT_PUBLIC_INITO_HOST}/en-us/checkout/${selectedModel.id}?${params.toString()}`;
    } else {
      params.set(
        "disc_code",
        modalContent?.couponCode || FALLBACK_DISCOUNT_CODES["og"]
      );
      // OG / iPhone-model checkout stays on Rails — the Node checkout only
      // maintains the NG product, so OG slugs would 400.
      window.location.href = `${process.env.NEXT_PUBLIC_INITO_HOST}/en-us/checkout/${selectedModel.id}?${params.toString()}`;
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger
        data-testid={testId}
        id={testId}
        onClick={handleCategorySelect}
        className="iphone-category"
      >
        {children}
      </SheetTrigger>
      <SheetContent
        className="flex h-full flex-col gap-6 px-0"
        overlayClassName="bg-black/50"
        sheetCloseTestId="modal-close-2"
      >
        <SheetHeader className="flex flex-col gap-3 px-6 py-4 pt-10 text-left pb-0">
          <SheetTitle
            className="!text-[32px] !font-medium !leading-8 !text-primary"
            id="choose-iphone-model-text"
            data-testid="choose-iphone-model-text"
          >
            Select your iPhone model
          </SheetTitle>
          <SheetDescription asChild>
            <div>
              <p className="relative inline !body-14 !text-secondary">
                Select one of the models below. This is to ensure your
                attachment clip will fit your phone’s dimensions.
              </p>
            </div>
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-4 overflow-auto">
          <div className="flex items-center justify-between px-6 py-3">
            <button
              className="-ml-1 flex items-center gap-[2px] hover:cursor-pointer"
              type="button"
              onClick={() => {
                setOpen(false);
                setSelectedModel(null);
              }}
              data-testid="go-back-button"
              id="go-back-button"
            >
              <ChevronRight className="h-4 w-4 rotate-180 text-primary" />
              <p className="text-sm font-medium leading-6 tracking-[-0.4px] text-primary">
                Go back
              </p>
            </button>
            <span className="rounded-[4px] bg-cream px-2 py-1 text-sm font-medium leading-6 tracking-[-0.4px]">
              Required
            </span>
          </div>
          <form className="flex flex-1 flex-col gap-4 px-6">
            <RadioGroup
              value={selectedModel}
              onValueChange={handleModelSelect}
              className="flex flex-1 flex-col gap-4"
              name={`model-${category.id}`}
            >
              {(iphoneModels as any)[category.id].map((model: any) => (
                <label
                  key={model.id}
                  className="flex cursor`-pointer items-center justify-between border-b border-b-[rgba(0,0,0,0.08)] px-1 py-4 hover:cursor-pointer"
                >
                  <span
                    className="text-[17px] font-medium leading-[26px] tracking-tight text-primary"
                    data-testid={model.name.toLowerCase().replaceAll(" ", "-")}
                    id={model.name.toLowerCase().replaceAll(" ", "-")}
                  >
                    {model.name}
                  </span>
                  <RadioGroupItem
                    value={model}
                    id={model.id}
                    className="iphone-model"
                  />
                </label>
              ))}
            </RadioGroup>
          </form>
        </div>
        <SheetFooter>
          <div className="flex w-full flex-col gap-4 border-t border-t-[rgba(0,0,0,0.08)] pt-6">
            <p className="px-6 text-[12px] font-medium leading-[18px] tracking-[-0.2px] text-grey">
              The First Generation Reader currently supports iPhone 7 and up.
              Android phones are not supported at the moment.
            </p>
            <div className="px-6">
              <Button
                type="submit"
                disabled={!selectedModel || isRedirecting}
                className={cn(
                  "w-full rounded-[12px]",
                  (!selectedModel || isRedirecting) &&
                    "bg-[#D4D1CE] text-secondary",
                  selectedModel &&
                    !isRedirecting &&
                    "bg-primary text-primary-foreground"
                )}
                onClick={handleCheckout}
                data-testid="checkout-now-button"
                id="checkout-now-button"
              >
                <span className="text-[17px] font-medium leading-[26px] tracking-[-0.4px] flex items-center justify-center gap-2">
                  {isRedirecting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Preparing your cart...
                    </>
                  ) : (
                    "Checkout now"
                  )}
                </span>
              </Button>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
